import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useFormat } from '@/lib/format';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api';
import {
  Plus,
  CaretLeft,
  CaretRight,
  FunnelSimple as Filter,
  CalendarBlank,
  DownloadSimple as Download,
  X,
  Briefcase,
  Trash,
  ChatCircle,
  ListChecks,
  LockKey,
  Warning,
  Clock,
  MapPin,
  Scales,
  Note,
  Users,
  Gavel,
  ArrowSquareOut,
  CheckCircle,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchTasks, type Task } from '@/lib/tasksApi';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useDiaryStore,
  dateKey,
  nextEventId,
  HOURS,
  KIND_LABEL,
  KIND_TONE,
  type CalEvent,
  type EventKind,
} from '@/lib/diaryStore';
import { useAuthStore } from '@/features/auth/store';
import { onCalendarUpdate, useWebSocketStore } from '@/lib/websocket';
import { useBarristersStore, ROLE_LABEL as BARRISTER_ROLE_LABEL, barristerNameMatches, type Barrister } from '@/lib/barristersStore';
import { useApiBarristersStore } from '@/lib/barristersApi';
import { fetchBarristerFreeBusy } from '@/lib/calendarApi';
import { busyHourColumns } from '@/lib/freeBusyGrid';
import { useMattersStore } from '@/lib/mattersStore';
import { useClerkWorkspace } from '@/lib/useClerkWorkspace';
import { canSeeEventContent, isAdmin, redactedEventLabel, type PrincipalLite } from '@/lib/permissions';
import { useBarristerBusy } from '@/lib/useBarristerBusy';
import { BarristerDayTimeline } from '@/components/shared/BarristerDayTimeline';
import { DoubleBookConfirm } from '@/components/shared/DoubleBookConfirm';
import { ScrollLock } from '@/components/shared/ScrollLock';

const HOUR_LABELS = ['09', '10', '11', '12', '13', '14', '15', '16', '17'];
const EMPTY_BUSY: ReadonlySet<number> = new Set();

const SLOT_STYLE: Record<string, string> = {
  hearing:      'bg-brand-200 hover:bg-brand-300',
  conference:   'bg-amber-200 hover:bg-amber-300',
  task:         'bg-violet-200 hover:bg-violet-300',
  brief_return: 'bg-red-200 hover:bg-red-300',
  personal:     'bg-emerald-200 hover:bg-emerald-300',
  '':           'bg-muted/30 hover:bg-brand-50',
};


export default function MasterDiaryPage() {
  // Dates and clock notation follow this clerk's own preferences.
  const { formatDate, formatWallTime } = useFormat();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clerkName = user ? `${user.firstName} ${user.lastName}` : 'Clerk';

  // Real barrister API data — provides user.id (UUID) for correct event ownership.
  // useBarristersStore already triggers auto-fetch; read the raw API records here
  // for the ownerId lookup in handleAllocate.
  const realBarristers = useApiBarristersStore((s) => s.barristers);
  const events = useDiaryStore((s) => s.events);
  const addEvent = useDiaryStore((s) => s.addEvent);
  const deleteEvent = useDiaryStore((s) => s.deleteEvent);
  const fetchEvents = useDiaryStore((s) => s.fetchEvents);
  const wsConnected = useWebSocketStore((s) => s.connected);
  const queryClient = useQueryClient();

  // Master Diary has TWO data sources: the matter-linked EVENTS grid (fetchEvents) and the opaque
  // free/busy overlay (the 'master-diary-freebusy' query below). A barrister's PERSONAL busy event
  // never lands in the events grid — it only moves the free/busy map — so a calendar:updated ping
  // that refetched only events left the "Busy" blocks stale until a manual refresh. Refresh BOTH on
  // every live signal so the clerk sees a barrister go busy the instant it happens — no refresh.
  const refreshAll = useCallback(() => {
    fetchEvents();
    queryClient.invalidateQueries({ queryKey: ['master-diary-freebusy'] });
  }, [fetchEvents, queryClient]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Polling + WebSocket subscription — re-attaches when socket connects/reconnects.
  useEffect(() => {
    const poll = setInterval(refreshAll, 300_000);
    const onVisible = () => { if (document.visibilityState === 'visible') refreshAll(); };
    document.addEventListener('visibilitychange', onVisible);
    const unsubWs = onCalendarUpdate(() => refreshAll());
    return () => { clearInterval(poll); document.removeEventListener('visibilitychange', onVisible); unsubWs(); };
  }, [refreshAll, wsConnected]);
  const matters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  useEffect(() => { fetchMatters(); }, [fetchMatters]);
  const caseOptions = matters.map((m) => ({ id: m.id, label: `${m.number} · ${m.name}` }));
  const { managedBarristers } = useClerkWorkspace();

  const principal: PrincipalLite = {
    name: clerkName,
    role: user?.memberType === 'admin' ? 'admin' : 'clerk',
    memberType: user?.memberType,
  };
  const viewerIsAdmin = isAdmin(principal);

  // Live roster from barristersStore — uses shortName for diary row label.
  // Keep the full profile around for fuzzy name matching on events.
  const allBarristers = useBarristersStore((s) => s.barristers);
  const ALL_BARRISTERS = useMemo(
    () =>
      allBarristers.map((b) => ({
        id: b.id,
        name: b.shortName ?? b.name,
        fullName: b.name,
        role: BARRISTER_ROLE_LABEL[b.role],
        profile: b as Barrister,
      })),
    [allBarristers],
  );

  const [dayOffset, setDayOffset] = useState(0);
  const [clicked, setClicked] = useState<{ barrister: string; hour: number } | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalEvent | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [hiddenBarristers, setHiddenBarristers] = useState<Set<string>>(new Set());

  const currentDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + dayOffset);
    return d;
  }, [dayOffset]);
  const currentDateKey = dateKey(currentDate);

  // Tasks delegated to this clerk. A barrister can create a task (private or not) and
  // assign it to a specific clerk; the clerk must be able to see it on their diary. The
  // master-diary grid only renders calendar EVENTS, and tasks never become events — so
  // without this panel a delegated task is invisible here. The API returns tasks where
  // the caller is the assignee regardless of matter scope or the isPrivate flag (see
  // tasks.service listTasks OR-scope + getTask isPersonallyConnected).
  const { data: myTasksData, isError: tasksError } = useQuery({
    queryKey: ['clerk-diary-tasks', user?.id],
    queryFn: () => fetchTasks({ assignedToId: user?.id ?? '', limit: 100, status: 'pending' as Task['status'] }),
    enabled: !!user?.id,
    staleTime: 60_000,
  });
  const myTasksForDay = useMemo(
    () =>
      (myTasksData?.tasks ?? [])
        .filter((t) => t.dueDate && t.dueDate.slice(0, 10) === currentDateKey)
        .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? '')),
    [myTasksData, currentDateKey],
  );

  // Opaque free/busy overlay: a barrister's PERSONAL (non-matter) events never reach the clerk's
  // diary (confidentiality), so the grid would show them as "free". We fetch each barrister's
  // occupied slots — time only, never any detail — and paint those hours as "Busy" so the clerk
  // can coordinate without seeing what the event is. Returns a map: barrister userId → busy hour columns.
  // Calendar times are stored as wall-clock-in-UTC (`${date}T${time}:00Z`) and read back in UTC
  // everywhere in the app (see calendarApi mapCalEvent). The window + cell maths below MUST use the
  // same UTC convention — using LOCAL `setHours` shifted every busy slot by the browser's offset
  // (e.g. a 12:10 event painted the 17:00 column in IST), so the clerk saw the wrong hour / no event.
  const dayStartISO = `${currentDateKey}T00:00:00Z`;
  const dayEndISO = `${currentDateKey}T23:59:59Z`;

  const { data: freeBusyByUser, isError: freeBusyError } = useQuery({
    queryKey: ['master-diary-freebusy', currentDateKey, realBarristers.map((b) => b.id).join(',')],
    enabled: realBarristers.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const map = new Map<string, Set<number>>();
      await Promise.all(
        realBarristers.map(async (rb) => {
          try {
            const slots = await fetchBarristerFreeBusy(rb.id, dayStartISO, dayEndISO);
            // UTC wall-clock cell boundaries — same convention the events are stored/read with.
            map.set(rb.user.id, busyHourColumns(slots, currentDateKey, HOUR_LABELS));
          } catch { /* one barrister's feed failing must not blank the whole grid */ }
        }),
      );
      return map;
    },
  });

  // Build the grid: for each barrister, match events by ownerId UUID first (exact),
  // falling back to fuzzy name matching for legacy events that pre-date the ownerId field.
  const grid = useMemo(() => {
    return ALL_BARRISTERS.map((b) => {
      // Resolve the User UUID for this barrister (needed for ownerId-based matching)
      const realBarr = realBarristers.find((rb) =>
        b.fullName.toLowerCase().includes(rb.user.lastName.toLowerCase()) ||
        `${rb.user.firstName} ${rb.user.lastName}`.toLowerCase() === b.fullName.toLowerCase(),
      );
      const barrUserId = realBarr?.user.id;

      const barEvents = events.filter((e) => {
        if (e.date !== currentDateKey) return false;
        // Prefer UUID match (authoritative) — fall back to name match for older events
        if (barrUserId && e.ownerId) return e.ownerId === barrUserId;
        return barristerNameMatches(e.barrister, b.profile);
      });
      // Map hours to events
      const slots: (CalEvent | null)[] = HOUR_LABELS.map((_, hourIdx) => {
        return barEvents.find((e) => hourIdx >= e.start && hourIdx < e.start + e.span) ?? null;
      });
      const busyHours = (barrUserId ? freeBusyByUser?.get(barrUserId) : undefined) ?? EMPTY_BUSY;
      return { ...b, barrUserId, events: barEvents, slots, busyHours };
    });
  }, [events, currentDateKey, realBarristers, freeBusyByUser]);

  const handleAllocate = async (barrister: string, hour: number, kind: EventKind, title: string, caseId: string, notes: string) => {
    // Look up the barrister's real User UUID so the event is stored under their
    // ownership — without this, the event would be owned by the clerk and never
    // appear in the barrister's diary.
    const realBarr = realBarristers.find((b) =>
      `${b.user.firstName} ${b.user.lastName}`.toLowerCase().includes(barrister.toLowerCase()) ||
      barrister.toLowerCase().includes(b.user.lastName.toLowerCase()),
    );
    // AWAIT before telling the clerk the barrister is booked. `addEvent` rolls back and
    // rethrows on refusal, and this call used to be fire-and-forget — the clerk saw
    // "booked at 10:00", the slot emptied again, and nobody was told. See MyDiaryPage.
    try {
      await addEvent({
        id: nextEventId(),
        date: currentDateKey,
        start: hour,
        span: 1,
        title,
        kind,
        tone: KIND_TONE[kind],
        caseId: caseId || undefined,
        notes: notes || undefined,
        ownerId: realBarr?.user.id,   // barrister owns this event
        barrister,
        createdBy: `${clerkName} (Clerk)`,
        status: 'confirmed',
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not book that slot — please try again'));
      return; // leave the slot editor open so the clerk can retry
    }

    toast.success(`${barrister} booked at ${HOUR_LABELS[hour]}:00 — ${title}`);
    setClicked(null);
  };

  const handleDeleteEvent = (eventId: string) => {
    deleteEvent(eventId);
    toast.success('Slot cleared');
    setSelectedEvent(null);
  };

  return (
    <div>
      {/* AN EMPTY GRID MUST NOT READ AS "EVERYONE IS FREE". The board below draws a free slot
          wherever it holds no event, so a failed tasks or free/busy fetch renders as a clear day
          for the whole of chambers — the single most misleading thing this screen could say to a
          clerk about to allocate work. Say the grid is incomplete instead. */}
      {(tasksError || freeBusyError) && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
        >
          <span className="font-semibold">
            Couldn't load {tasksError && freeBusyError ? 'tasks and availability' : tasksError ? "today's tasks" : 'availability'}.
          </span>
          <span className="text-amber-700 dark:text-amber-300">
            This is a connection problem — free slots below may not really be free.
          </span>
        </div>
      )}
      <PageHeader
        title="Master diary"
        subtitle="Click any cell to allocate. Changes sync to barrister diaries instantly."
        actions={
          <>
            <Button variant="outline" size="md" leftIcon={<Download className="h-4 w-4" />} disabled>Court list PDF</Button>
            <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate('/clerk/schedule')}>
              Schedule hearing
            </Button>
          </>
        }
      />

      {/* Toolbar */}
      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setDayOffset(0)}>Today</Button>
            <button onClick={() => setDayOffset((d) => d - 1)} className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground">
              <CaretLeft className="h-4 w-4" />
            </button>
            <button onClick={() => setDayOffset((d) => d + 1)} className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground">
              <CaretRight className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 text-base font-semibold text-foreground">
            <CalendarBlank className="h-4 w-4 text-muted-foreground" />
            {formatDate(currentDate, { style: 'long' })}
          </div>
          <div className="relative ml-auto">
            <Button
              variant={hiddenBarristers.size > 0 ? 'primary' : 'outline'}
              size="sm"
              leftIcon={<Filter className="h-3.5 w-3.5" />}
              onClick={() => setFilterOpen((v) => !v)}
            >
              {hiddenBarristers.size > 0 ? `${ALL_BARRISTERS.length - hiddenBarristers.size} shown` : 'Filters'}
            </Button>
            {filterOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFilterOpen(false)} />
                <div className="absolute right-0 top-9 z-50 min-w-[200px] rounded-lg border border-border bg-card p-2 shadow-lg">
                  <div className="mb-2 flex items-center justify-between px-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Show barristers</span>
                    {hiddenBarristers.size > 0 && (
                      <button className="text-xs text-brand-600 dark:text-brand-400 hover:underline" onClick={() => setHiddenBarristers(new Set())}>Show all</button>
                    )}
                  </div>
                  {ALL_BARRISTERS.map((b) => (
                    <label key={b.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={!hiddenBarristers.has(b.id)}
                        onChange={() => {
                          setHiddenBarristers((prev) => {
                            const next = new Set(prev);
                            if (next.has(b.id)) next.delete(b.id); else next.add(b.id);
                            return next;
                          });
                        }}
                        className="h-3.5 w-3.5 rounded border-border text-brand-600 dark:text-brand-400"
                      />
                      <span className="text-sm text-foreground">{b.name}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Time grid — reads from shared diaryStore */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="sticky left-0 z-10 bg-muted/30 px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Barrister
                </th>
                {HOUR_LABELS.map((h) => (
                  <th key={h} className="px-2 py-3 text-center text-[10px] font-semibold tabular text-muted-foreground">
                    {/* The clerk's own 12/24-hour choice. The COLUMN still means the practice's
                        working hour — notation only, never a timezone shift. */}
                    {formatWallTime(`${h}:00`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {grid.filter((row) => !hiddenBarristers.has(row.id)).map((row) => (
                <tr key={row.id}>
                  <td className="sticky left-0 z-10 bg-card px-4 py-3">
                    <div className="text-sm font-medium text-foreground">{row.name}</div>
                    <div className="text-xs text-muted-foreground">{row.role} · {row.events.length} event{row.events.length !== 1 ? 's' : ''}</div>
                  </td>
                  {row.slots.map((event, j) => {
                    // Privacy: is this barrister managed by the clerk? does the event belong to a matter we can see?
                    // Match on the barrister USER UUID first (authoritative); fall back to
                    // suffix-tolerant name matching. An exact name Set lookup silently fails
                    // for silks because the roster labels them "… KC" while the workspace name
                    // has no suffix — which wrongly redacted a silk's diary from their own
                    // designated clerk. UUID/fuzzy matching mirrors canSeeMatter.
                    const managesBarrister =
                      viewerIsAdmin ||
                      managedBarristers.some((mb) =>
                        (mb.userId && row.barrUserId && mb.userId === row.barrUserId) ||
                        mb.name === row.fullName ||
                        row.fullName.includes(mb.name) ||
                        mb.name.includes(row.name),
                      );
                    const canSeeContent = event
                      ? managesBarrister && canSeeEventContent(principal, event, matters, managedBarristers)
                      : false;
                    // Empty slot that the barrister is actually occupied in (a personal event the
                    // clerk can't see the content of) — show an opaque "Busy" block, no detail.
                    const busyEmpty = !event && row.busyHours.has(j);
                    const displayLabel = event
                      ? canSeeContent
                        ? event.title
                        : redactedEventLabel(event)
                      : '';
                    return (
                      <td key={j} className="px-1 py-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (!event) {
                              setClicked({ barrister: row.name, hour: j });
                            } else if (canSeeContent) {
                              setSelectedEvent(event);
                            } else {
                              toast.info(
                                `${row.name} is ${redactedEventLabel(event).toLowerCase()} at ${HOUR_LABELS[j]}:00 — you're not on this matter's team.`,
                              );
                            }
                          }}
                          className={`relative mx-auto block h-9 w-full overflow-hidden rounded transition focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                            event
                              ? event.status === 'tentative'
                                ? 'cursor-pointer border-2 border-dashed border-amber-400 bg-amber-100 dark:bg-amber-500/15 hover:bg-amber-200'
                                : canSeeContent
                                ? `cursor-pointer ${SLOT_STYLE[event.kind] ?? SLOT_STYLE['']}`
                                : `cursor-help ${SLOT_STYLE[event.kind] ?? SLOT_STYLE['']} opacity-70`
                              : busyEmpty
                              ? 'cursor-pointer border border-border bg-muted text-muted-foreground hover:bg-slate-300'
                              : `cursor-pointer ${SLOT_STYLE['']}`
                          }`}
                          title={
                            event
                              ? canSeeContent
                                ? `${row.name} · ${HOUR_LABELS[j]}:00 · ${event.title}${
                                    event.status === 'tentative' ? ' (TENTATIVE)' : ''
                                  }${event.createdBy ? ' (by ' + event.createdBy + ')' : ''}`
                                : `${row.name} · ${HOUR_LABELS[j]}:00 · ${redactedEventLabel(event)} — restricted (not on team)`
                              : busyEmpty
                              ? `${row.name} · ${HOUR_LABELS[j]}:00 · Busy — barrister is occupied (private)`
                              : `${row.name} · ${HOUR_LABELS[j]}:00 · Free — click to allocate`
                          }
                        >
                          {event && (
                            <span className="absolute inset-0 flex items-center justify-center truncate px-1 text-[8px] font-semibold leading-tight">
                              {displayLabel.length > 15 ? displayLabel.slice(0, 15) + '…' : displayLabel}
                            </span>
                          )}
                          {busyEmpty && (
                            <span className="absolute inset-0 flex items-center justify-center text-[8px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Busy
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-border px-6 py-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-brand-200" /> Hearing</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-200" /> Conference</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-border bg-muted" /> Busy (private)</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-violet-200" /> Task</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-red-200" /> Brief return</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-200" /> Personal</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-muted/30" /> Free</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-dashed border-amber-400 bg-amber-100 dark:bg-amber-500/15" /> Tentative</span>
        </div>
      </Card>

      {/* Tasks delegated to this clerk, due on the selected day. The grid above shows
          barristers' calendar events only; a task assigned to the clerk (incl. private)
          would otherwise never surface on the master diary. */}
      {myTasksForDay.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 border-b border-border px-6 py-3">
            <ListChecks className="h-4 w-4 text-violet-500" />
            <span className="text-sm font-semibold text-foreground">Tasks assigned to me</span>
            <span className="text-xs text-muted-foreground">
              {formatDate(currentDate, { style: 'medium' })}
            </span>
            <Badge>{myTasksForDay.length}</Badge>
          </div>
          <CardContent className="divide-y divide-border px-0 py-0">
            {myTasksForDay.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => { if (t.case) navigate(`/clerk/matters/${t.case.id}`); else navigate('/clerk/tasks'); }}
                className="flex w-full items-center gap-3 px-6 py-3 text-left transition hover:bg-muted/30"
              >
                {t.isPrivate && <LockKey size={14} weight="fill" className="shrink-0 text-brand-600 dark:text-brand-400" aria-label="Private task" />}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{t.title}</div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {t.case ? `${t.case.caseNumber} · ${t.case.caseName}` : 'No matter'}
                    {' · from '}
                    {t.createdBy.firstName} {t.createdBy.lastName}
                  </div>
                </div>
                {t.priority === 'urgent' || t.priority === 'high' ? (
                  <Badge variant="danger">{t.priority}</Badge>
                ) : null}
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Allocate modal — creates event in shared store */}
      {clicked && (
        <AllocateModal
          barrister={clicked.barrister}
          barristerId={
            // Pass the BARRISTER id (not user.id): useBarristerBusy → fetchBarristerFreeBusy hits the
            // free-busy endpoint, which resolves prisma.barrister.findFirst({ where: { id } }). Passing
            // user.id matched no barrister → free/busy returned [] → the clash check saw an empty diary
            // and said "Slot is clear" even when the grid showed the barrister busy. Must be Barrister.id.
            realBarristers.find((b) =>
              `${b.user.firstName} ${b.user.lastName}`.toLowerCase().includes(clicked.barrister.toLowerCase()) ||
              clicked.barrister.toLowerCase().includes(b.user.lastName.toLowerCase()),
            )?.id ?? null
          }
          hour={clicked.hour}
          date={currentDateKey}
          dateLabel={formatDate(currentDate, { style: 'medium' })}
          caseOptions={caseOptions}
          onClose={() => setClicked(null)}
          onAllocate={(kind, title, caseId, notes) => handleAllocate(clicked.barrister, clicked.hour, kind, title, caseId, notes)}
        />
      )}

      {/* Event detail modal */}
      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          clerkName={clerkName}
          onClose={() => setSelectedEvent(null)}
          onDelete={() => handleDeleteEvent(selectedEvent.id)}
          onConfirm={() => {
            useDiaryStore.getState().updateEvent(selectedEvent.id, { status: 'confirmed' });
            toast.success(`Conference confirmed`);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Allocate modal
// ─────────────────────────────────────────────────────────────

function AllocateModal({
  barrister,
  barristerId,
  hour,
  date,
  dateLabel,
  caseOptions,
  onClose,
  onAllocate,
}: {
  barrister: string;
  barristerId: string | null;
  hour: number;
  date: string;
  dateLabel: string;
  caseOptions: { id: string; label: string }[];
  onClose: () => void;
  onAllocate: (kind: EventKind, title: string, caseId: string, notes: string) => void;
}) {
  // Diary allocation may ONLY create calendar-native blocks (personal/meeting/travel/away).
  // Hearings/conferences are domain objects created from their own workflows ("Schedule
  // hearing" → /clerk/schedule; conferences via the conference flow) — never minted here.
  const [kind, setKind] = useState<EventKind>('personal');
  const [title, setTitle] = useState('');
  const [caseId, setCaseId] = useState('');
  const [notes, setNotes] = useState('');

  // Wall-clock-in-UTC, matching how events are stored/read app-wide (`${date}T${time}:00Z`). Building
  // this with local `new Date(...).toISOString()` shifted the clash check by the browser offset, so
  // the warning fired against the wrong hour (or missed a real clash) for clerks in non-UTC zones.
  const startIso = useMemo(() => {
    if (!date) return null;
    return `${date}T${HOUR_LABELS[hour]}:00:00Z`;
  }, [date, hour]);

  const { getConflicts, getEventsForDate } = useBarristerBusy(barristerId, startIso ? [startIso] : []);
  const conflicts = useMemo(
    () => (startIso ? getConflicts(startIso, 60) : []),
    [startIso, getConflicts],
  );
  const dayEvents = useMemo(
    () => (startIso ? getEventsForDate(startIso) : []),
    [startIso, getEventsForDate],
  );
  // Double-booking: warn the clerk (never block) when the slot they're allocating for the barrister
  // overlaps something already in that barrister's diary. Same warn-and-confirm pattern as My Diary.
  const overlaps = useMemo(() => conflicts.filter((c) => c.kind === 'overlap'), [conflicts]);
  const [confirmClash, setConfirmClash] = useState(false);
  const doAllocate = () => onAllocate(kind, title.trim(), caseId, notes.trim());
  const attemptAllocate = () => {
    if (overlaps.length > 0) { setConfirmClash(true); return; }
    doAllocate();
  };

  const inputCls = 'h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="w-full max-w-lg overflow-hidden shadow-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-border bg-brand-50 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-card text-sm font-semibold uppercase text-brand-700 dark:text-brand-300">
              {barrister.split(' ').map((p) => p[0]).join('').slice(0, 2)}
            </div>
            <div>
              <div className="text-base font-semibold text-foreground">Allocate: {barrister}</div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                <span>{dateLabel}</span>
                <span>·</span>
                <span className="">{HOUR_LABELS[hour]}:00</span>
              </div>
            </div>
          </div>
          <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Event type</label>
            <div className="grid grid-cols-4 gap-1.5">
              {([
                { k: 'personal' as EventKind, label: 'Personal', icon: CalendarBlank },
                { k: 'meeting' as EventKind,  label: 'Meeting',  icon: ChatCircle },
                { k: 'travel' as EventKind,   label: 'Travel',   icon: Briefcase },
                { k: 'leave' as EventKind,    label: 'Away',     icon: CalendarBlank },
              ]).map((opt) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setKind(opt.k)}
                    className={`flex flex-col items-center gap-1 rounded-md border p-2 transition ${
                      kind === opt.k ? 'border-brand-600 bg-brand-50' : 'border-border hover:border-brand-200 hover:bg-muted/40'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${kind === opt.k ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'}`} />
                    <span className="text-[10px] font-semibold text-foreground">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Title *</label>
            <input type="text" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="e.g. R v Whitfield — Court 4, Smith J" />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Link to matter</label>
            <select value={caseId} onChange={(e) => setCaseId(e.target.value)} className={inputCls}>
              <option value="">— None —</option>
              {caseOptions.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="h-20 w-full resize-none rounded-md border border-border bg-card p-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100" placeholder="Optional notes" />
          </div>

          {/* Schedule conflict check */}
          {startIso && barristerId && (
            <BarristerDayTimeline
              events={dayEvents}
              proposedStart={startIso}
              proposedDurationMinutes={60}
              conflicts={conflicts}
              utc
              proposedLabel={KIND_LABEL[kind]}
            />
          )}

          {startIso && barristerId && overlaps.length > 0 && (
            <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
              <Warning className="h-3.5 w-3.5" weight="fill" />
              {barrister} is already booked at this time \u2014 you'll be asked to confirm.
            </p>
          )}

          <div className="rounded-md border border-blue-100 bg-blue-50 dark:bg-blue-500/10 p-2.5 text-xs text-blue-900 dark:text-blue-200">
            This will appear on <strong>{barrister}{'\u2019'}s diary</strong> immediately, attributed to you.
          </div>
        </CardContent>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-3">
          <Button variant="outline" size="md" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" disabled={!title.trim()} onClick={attemptAllocate}>
            Allocate
          </Button>
        </div>
      </Card>
      </div>

      {confirmClash && (
        <DoubleBookConfirm
          clashes={overlaps.map((c) => ({ title: c.event.title, startAt: c.event.startAt, endAt: c.event.endAt }))}
          onConfirm={() => { setConfirmClash(false); doAllocate(); }}
          onCancel={() => setConfirmClash(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Event detail modal
// ─────────────────────────────────────────────────────────────

// Event-kind → Phosphor icon for the detail popup's type tile.
const KIND_ICON: Record<string, PhosphorIcon> = {
  hearing: Gavel,
  conference: ChatCircle,
  meeting: Users,
  personal: CalendarBlank,
  travel: MapPin,
  leave: CalendarBlank,
  task: ListChecks,
  brief_return: Briefcase,
};

// Icon-anchored detail row used across the event-detail popup body.
function DetailRow({ icon: Icon, label, children }: { icon: PhosphorIcon; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" weight="duotone" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-0.5 text-sm text-foreground">{children}</div>
      </div>
    </div>
  );
}

function EventModal({
  event,
  onClose,
  onDelete,
  onConfirm,
}: {
  event: CalEvent;
  clerkName: string;
  onClose: () => void;
  onDelete: () => void;
  onConfirm: () => void;
}) {
  const isTentative = event.status === 'tentative';
  const navigate = useNavigate();
  const KindIcon = KIND_ICON[event.kind] ?? CalendarBlank;
  const matterLine = event.matter
    ? [event.matter.number, event.matter.name, event.hearingType, event.matter.court].filter(Boolean).join(' · ')
    : null;
  const dateStr = new Date(event.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const timeStr = `${HOURS[event.start]} — ${HOURS[Math.min(event.start + event.span, HOURS.length - 1)]} · ${event.span}h`;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="w-full max-w-md overflow-hidden shadow-pop" onClick={(e) => e.stopPropagation()}>
        {/* Neutral header (design-consistency): the event type is conveyed by the badges below, so
            the header no longer paints a saturated tone band. A tentative event keeps a subtle amber
            wash because it signals a required action, not merely a category. */}
        <div className={`flex items-start gap-3 border-b border-border px-5 py-4 ${isTentative ? 'bg-amber-50 dark:bg-amber-500/10' : 'bg-card'}`}>
          {/* Type-icon tile — conveys the event kind without a saturated header band. */}
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isTentative ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' : 'bg-muted text-brand-600 dark:text-brand-400'}`}>
            <KindIcon className="h-5 w-5" weight="duotone" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
              <span className="font-semibold text-foreground">{KIND_LABEL[event.kind]}</span>
              {event.barrister && <span className="truncate text-muted-foreground">· {event.barrister}</span>}
              {isTentative ? (
                <span className="inline-flex items-center gap-1 font-medium text-amber-700 dark:text-amber-400"><Warning className="h-3 w-3" weight="fill" /> Tentative</span>
              ) : event.status === 'confirmed' ? (
                <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400"><CheckCircle className="h-3 w-3" weight="fill" /> Confirmed</span>
              ) : null}
            </div>
            {/* truncate needs min-w-0 on this column (above) to clip rather than run to the edge;
                title= reveals the full text on hover, per request. */}
            <div className="mt-1 truncate text-lg font-semibold tracking-tight text-foreground" title={event.title}>{event.title}</div>
            {event.sub && <div className="truncate text-xs text-muted-foreground" title={event.sub}>{event.sub}</div>}
            {matterLine && (
              <button
                type="button"
                title={matterLine}
                onClick={() => { if (event.caseId) { navigate(`/clerk/matters/${event.caseId}`); onClose(); } }}
                className="mt-1 flex max-w-full items-center gap-1 text-xs font-medium capitalize text-brand-600 dark:text-brand-400 hover:underline"
              >
                <ArrowSquareOut className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{matterLine}</span>
              </button>
            )}
          </div>
          <button className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="space-y-3 p-6">
          {isTentative && (
            <div className="rounded-md border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
              <strong>{event.barrister}</strong> proposed this conference. Review the details and confirm, suggest a new time, or decline.
            </div>
          )}

          <div className="space-y-3">
            <DetailRow icon={CalendarBlank} label="Date">{dateStr}</DetailRow>
            <DetailRow icon={Clock} label="Time">{timeStr}</DetailRow>
            <DetailRow icon={Scales} label="Barrister">{event.barrister ?? 'Unassigned'}</DetailRow>
            {event.location && <DetailRow icon={MapPin} label="Location">{event.location}</DetailRow>}
            {event.attendees && <DetailRow icon={Users} label="Attendees">{event.attendees}</DetailRow>}
            {event.notes && (
              <DetailRow icon={Note} label="Notes / Agenda">
                <span className="whitespace-pre-wrap">{event.notes}</span>
              </DetailRow>
            )}
          </div>
          {event.createdBy && (
            <div className="rounded-md border border-border bg-muted/30 p-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Proposed by</div>
              <div className="mt-0.5 text-sm font-medium text-foreground">{event.createdBy}</div>
            </div>
          )}
        </CardContent>

        <div className="flex items-center justify-between border-t border-border px-6 py-3">
          <button onClick={onDelete} className="inline-flex items-center gap-1.5 text-xs font-medium text-danger hover:underline">
            <Trash className="h-3.5 w-3.5" /> {isTentative ? 'Decline' : 'Delete'}
          </button>
          <div className="flex items-center gap-2">
            {isTentative && (
              <Button variant="primary" size="sm" onClick={onConfirm}>
                Confirm conference
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose}>{isTentative ? 'Review later' : 'Done'}</Button>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
}
